import * as XLSX from 'xlsx';
import {
  RfqExcelFormData,
  RfqExcelImportPreview,
  RfqExcelItem,
  RfqExcelLookupData,
  RFQ_ITEM_COLUMNS,
} from './rfq-excel.types';
import { parseHeaderSheet } from './rfq-excel-template';
import { RFQ_EXCEL_NON_FORM_HEADER_KEYS } from './rfq-excel-aliases';
import {
  extractFlexibleHeader,
  extractFlexibleItems,
  isSkipSheetName,
  parseFlexibleRfqWorkbook,
  detectUnsupportedRfqListWorkbook,
} from './rfq-excel-flexible-parse';
import {
  resolveRfqImportMasterData,
  type RfqImportFieldResolution,
} from './rfq-import-master-resolve';
import { resolveRfqImportPartFinder } from './rfq-import-partfinder-resolve';

function normalize(value: string): string {
  return value.toLowerCase().trim();
}

function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function parseDate(value: string): string {
  if (!value) return '';
  // Excel serial date number
  if (/^\d+(\.\d+)?$/.test(value)) {
    const serial = parseFloat(value);
    if (serial > 30000 && serial < 60000) {
      const date = XLSX.SSF.parse_date_code(serial);
      if (date) {
        const y = date.y;
        const m = String(date.m).padStart(2, '0');
        const d = String(date.d).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
    }
  }
  const parsed = new Date(value);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0];
  }
  return value;
}

function splitList(value: string): string[] {
  return value
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function findSheet(workbook: XLSX.WorkBook, ...names: string[]): XLSX.WorkSheet | null {
  for (const name of names) {
    const exact = workbook.Sheets[name];
    if (exact) return exact;
  }
  const normalizedTargets = names.map(normalizeKey);
  for (const sheetName of workbook.SheetNames) {
    const trimmed = sheetName.trim();
    if (normalizedTargets.includes(normalizeKey(trimmed))) {
      return workbook.Sheets[sheetName];
    }
  }
  return null;
}

function formatSheetList(workbook: XLSX.WorkBook): string {
  return workbook.SheetNames.map((n) => `"${n}"`).join(', ');
}

/** Official-template items parse (alias-aware via flexible extractor). */
function parseItemsSheet(sheet: XLSX.WorkSheet): {
  items: RfqExcelItem[];
  warnings: string[];
  unsupportedQuotationFields: string[];
} {
  const extracted = extractFlexibleItems(sheet);
  return {
    items: extracted.items,
    warnings: extracted.warnings,
    unsupportedQuotationFields: extracted.unsupportedQuotationFields,
  };
}

/**
 * Merge additional unambiguous header fields from other sheets without
 * overwriting values already taken from a higher-priority source.
 */
function mergeSupplementalHeaderFields(
  workbook: XLSX.WorkBook,
  rawHeader: Record<string, string>,
  excludeSheets: Array<XLSX.WorkSheet | null | undefined>
): string[] {
  const unmatched: string[] = [];
  for (const sheetName of workbook.SheetNames) {
    if (isSkipSheetName(sheetName)) continue;
    const sheet = workbook.Sheets[sheetName];
    if (!sheet || excludeSheets.includes(sheet)) continue;
    const extracted = extractFlexibleHeader(sheet);
    for (const [key, value] of Object.entries(extracted.fields)) {
      if (!value?.trim()) continue;
      if (rawHeader[key]) continue;
      rawHeader[key] = value;
    }
    unmatched.push(...extracted.unmatchedLabels);
  }
  return unmatched;
}

function buildFormPatchFromRawHeader(
  rawHeader: Record<string, string>,
  lookup: RfqExcelLookupData,
  warnings: string[],
  matchedFields: string[],
  unmatchedFields: string[]
): {
  formPatch: Partial<RfqExcelFormData> & Record<string, unknown>;
  vendorNamesRaw: string;
  vesselResolution?: RfqImportFieldResolution;
} {
  const formPatch: Partial<RfqExcelFormData> & Record<string, unknown> = {};
  let vesselResolution: RfqImportFieldResolution | undefined;

  // Extracted but not applied to Create RFQ form (system assigns RFQ number)
  if (rawHeader.rfqNumber) {
    unmatchedFields.push('RFQ Number');
    warnings.push(
      `RFQ Number "${rawHeader.rfqNumber}" was recognized but is assigned by the system on create — not applied`
    );
  }

  // Quote deadline
  if (rawHeader.quoteDeadline) {
    formPatch.quoteDeadline = parseDate(rawHeader.quoteDeadline);
    matchedFields.push('Quote Deadline');
  }

  // Vessel — resolve to _id (fleet lookup; not product hierarchy)
  const vesselInput = rawHeader.vesselName ?? '';
  if (vesselInput) {
    const key = normalize(vesselInput);
    const byName = lookup.vessels.find((v) => normalize(v.name) === key);
    const byImo = lookup.vessels.find((v) => v.imoNumber && normalize(v.imoNumber) === key);
    const vessel = byName ?? byImo;
    if (vessel) {
      formPatch.vesselName = vessel._id;
      formPatch.imoNo = vessel.imoNumber ?? '';
      matchedFields.push('Vessel Name');
      vesselResolution = {
        field: 'vessel',
        importedValue: vesselInput,
        resolvedValue: vessel.name,
        resolvedId: vessel._id,
        status: 'matched',
      };
    } else {
      unmatchedFields.push('Vessel Name');
      const message = `Vessel "${vesselInput}" not found in fleet — left empty`;
      warnings.push(message);
      vesselResolution = {
        field: 'vessel',
        importedValue: vesselInput,
        status: 'unresolved',
        message,
      };
    }
  }

  // Provisional master-data fields — resolved by resolveRfqImportMasterData
  if (rawHeader.department) formPatch.department = rawHeader.department as RfqExcelFormData['department'];
  if (rawHeader.category) formPatch.category = rawHeader.category;
  if (rawHeader.subCategory) formPatch.subCategory = rawHeader.subCategory;
  if (rawHeader.brand) formPatch.brand = rawHeader.brand;
  if (rawHeader.model) formPatch.model = rawHeader.model;

  const vendorNamesRaw = rawHeader.vendors ?? '';

  const portInput = rawHeader.portNameInput ?? rawHeader.portName ?? '';
  if (portInput) formPatch.portName = portInput;

  const agentInput = rawHeader.portAgentNameInput ?? rawHeader.portAgentName ?? '';
  if (agentInput) formPatch.portAgentName = agentInput;

  // Optional scalar fields
  const scalarMap: Partial<Record<string, keyof RfqExcelFormData>> = {
    revisionNo: 'revisionNo',
    currency: 'currency',
    urgencyLevel: 'urgencyLevel',
    quoteValidityDays: 'quoteValidityDays',
    requisitionRef: 'requisitionRef',
    equipmentTags: 'equipmentTags',
    estimatedTotalCost: 'estimatedTotalCost',
    remarks: 'remarks',
    contactName: 'contactName',
    contactEmail: 'contactEmail',
    contactPhone: 'contactPhone',
    contactDepartment: 'contactDepartment',
    deliveryRequiredAt: 'deliveryRequiredAt',
    deliveryType: 'deliveryType',
    creditType: 'creditType',
    paymentTerm: 'paymentTerm',
    insuranceTerm: 'insuranceTerm',
    taxTerm: 'taxTerm',
    transportTerm: 'transportTerm',
    deliveryTerm: 'deliveryTerm',
    packingTerm: 'packingTerm',
    requiredPaymentTerms: 'requiredPaymentTerms',
    packingInstructions: 'packingInstructions',
    partialDeliveryAllowed: 'partialDeliveryAllowed',
    imoNo: 'imoNo',
  };

  for (const [rawKey, formKey] of Object.entries(scalarMap)) {
    if (RFQ_EXCEL_NON_FORM_HEADER_KEYS.has(rawKey)) continue;
    const val = rawHeader[rawKey];
    if (val) {
      (formPatch as Record<string, unknown>)[formKey] = val;
    }
  }

  if (rawHeader.leadDate) formPatch.leadDate = parseDate(rawHeader.leadDate);
  if (rawHeader.portEtaDate) formPatch.portEtaDate = parseDate(rawHeader.portEtaDate);

  if (rawHeader.requiredCertificates) {
    formPatch.requiredCertificates = splitList(rawHeader.requiredCertificates);
  }
  if (rawHeader.requiredDocuments) {
    formPatch.requiredDocuments = splitList(rawHeader.requiredDocuments);
  }

  return { formPatch, vendorNamesRaw, vesselResolution };
}

/** Raw source values for the review UI — never resolved or rewritten. */
function buildExtractedFields(rawHeader: Record<string, string>): Record<string, string> {
  const pick = (...keys: string[]) =>
    keys.map((k) => String(rawHeader[k] ?? '').trim()).find((v) => v.length > 0) ?? '';
  const out: Record<string, string> = {
    rfqNumber: pick('rfqNumber'),
    quoteDeadline: pick('quoteDeadline'),
    department: pick('department'),
    vessel: pick('vesselName'),
    category: pick('category'),
    subCategory: pick('subCategory'),
    brand: pick('brand'),
    model: pick('model'),
    vendors: pick('vendors'),
    port: pick('portNameInput', 'portName'),
    portAgent: pick('portAgentNameInput', 'portAgentName'),
  };
  for (const key of Object.keys(out)) {
    if (!out[key]) delete out[key];
  }
  return out;
}

export async function parseRfqExcelFile(
  file: File,
  lookup: RfqExcelLookupData
): Promise<RfqExcelImportPreview> {
  const warnings: string[] = [];
  const errors: string[] = [];
  const matchedFields: string[] = [];
  const unmatchedFields: string[] = [];

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });

  if (!workbook.SheetNames.length) {
    errors.push('The Excel file has no worksheets.');
    return {
      formPatch: {},
      items: [],
      warnings,
      errors,
      summary: { itemCount: 0, vendorCount: 0, matchedFields, unmatchedFields },
    };
  }

  const listExportError = detectUnsupportedRfqListWorkbook(workbook);
  if (listExportError) {
    errors.push(listExportError);
    return {
      formPatch: {},
      items: [],
      warnings,
      errors,
      summary: { itemCount: 0, vendorCount: 0, matchedFields, unmatchedFields },
      source: 'excel',
      needsManualReview: true,
    };
  }

  const officialHeaderSheet = findSheet(workbook, 'RFQ_Header', 'Header', 'RFQ Header');
  const officialItemsSheet = findSheet(
    workbook,
    'RFQ_Items',
    'Items',
    'RFQ Items',
    'Line Items',
    'LineItems'
  );

  let rawHeader: Record<string, string> = {};
  let items: RfqExcelItem[] = [];
  const unsupportedQuotationFields: string[] = [];

  const hasOfficialPair = Boolean(officialHeaderSheet && officialItemsSheet);

  if (hasOfficialPair) {
    // Support both tabular (row of titles + values) and legacy Field|Value layouts
    const flexHeader = extractFlexibleHeader(officialHeaderSheet!);
    rawHeader = { ...flexHeader.fields };
    if (Object.keys(rawHeader).length === 0) {
      rawHeader = parseHeaderSheet(officialHeaderSheet!);
    }
    for (const label of flexHeader.unmatchedLabels) {
      if (!unmatchedFields.includes(label)) unmatchedFields.push(label);
    }

    const parsedItems = parseItemsSheet(officialItemsSheet!);
    items = parsedItems.items;
    warnings.push(...parsedItems.warnings);
    for (const label of parsedItems.unsupportedQuotationFields ?? []) {
      if (!unsupportedQuotationFields.includes(label)) unsupportedQuotationFields.push(label);
    }

    // Unambiguous supplemental fields from other sheets (do not overwrite)
    const extraUnmatched = mergeSupplementalHeaderFields(workbook, rawHeader, [
      officialHeaderSheet,
      officialItemsSheet,
    ]);
    for (const label of extraUnmatched) {
      // Ignore Field_Map / reference noise
      if (/^(field label|form key|type|name|extra)$/i.test(label.trim())) continue;
      if (!unmatchedFields.includes(label)) unmatchedFields.push(label);
    }

    if (Object.keys(rawHeader).length === 0) {
      errors.push(
        `RFQ_Header sheet found but no recognizable fields. Found sheets: ${formatSheetList(workbook)}.`
      );
    }
    if (items.length === 0) {
      errors.push(
        'No valid item rows found in RFQ_Items sheet. Need columns Description, Quantity, and UOM (or aliases).'
      );
    }
  } else {
    // Flexible multi-layout / multi-sheet path
    const flexible = parseFlexibleRfqWorkbook(workbook);
    warnings.push(...flexible.warnings);
    errors.push(...flexible.errors);
    rawHeader = flexible.rawHeader;
    items = flexible.items;

    for (const label of flexible.unmatchedHeaderLabels) {
      if (!unmatchedFields.includes(label)) unmatchedFields.push(label);
    }
    for (const label of flexible.unsupportedQuotationFields) {
      if (!unsupportedQuotationFields.includes(label)) unsupportedQuotationFields.push(label);
    }
  }

  if (errors.length) {
    return {
      formPatch: {},
      items: [],
      warnings,
      errors,
      summary: { itemCount: 0, vendorCount: 0, matchedFields, unmatchedFields },
    };
  }

  const {
    formPatch: provisionalPatch,
    vendorNamesRaw,
    vesselResolution,
  } = buildFormPatchFromRawHeader(
    rawHeader,
    lookup,
    warnings,
    matchedFields,
    unmatchedFields
  );

  const masterResolved = await resolveRfqImportMasterData({
    formPatch: provisionalPatch,
    vendorNamesRaw,
    lookup,
  });

  warnings.push(...masterResolved.warnings);

  for (const res of masterResolved.resolutions) {
    if (res.status !== 'matched') continue;
    const label =
      res.field === 'subCategory'
        ? 'Sub Category'
        : res.field === 'portAgent'
          ? 'Port Agent'
          : res.field === 'port'
            ? 'Port Name'
            : res.field === 'vendors'
              ? 'Vendors'
              : res.field.charAt(0).toUpperCase() + res.field.slice(1);
    if (res.field === 'vendors') {
      if (!matchedFields.includes('Vendors')) matchedFields.push('Vendors');
      continue;
    }
    if (!matchedFields.includes(label)) matchedFields.push(label);
  }

  const formPatch = masterResolved.formPatch;

  // Validate-only PartFinder check (never mutates items or header)
  let partfinderParts = lookup.partFinderParts ?? [];
  if ((!partfinderParts || partfinderParts.length === 0) && lookup.getPartFinderParts) {
    partfinderParts = await lookup.getPartFinderParts();
  }
  const partfinder = resolveRfqImportPartFinder({
    items,
    parts: partfinderParts,
    formPatch,
  });
  warnings.push(...partfinder.warnings);

  // Required RFQ fields: distinguish "not in source" vs "provided but unresolved"
  const fieldsNotProvided: string[] = [];
  const fieldsUnresolved: Array<{ field: string; providedValue: string }> = [];

  const requiredChecks: Array<{
    label: string;
    rawKeys: string[];
    isResolved: () => boolean;
  }> = [
    {
      label: 'Quote Deadline',
      rawKeys: ['quoteDeadline'],
      isResolved: () => Boolean(formPatch.quoteDeadline),
    },
    {
      label: 'Department',
      rawKeys: ['department'],
      isResolved: () => Boolean(formPatch.department),
    },
    {
      label: 'Category',
      rawKeys: ['category'],
      isResolved: () => Boolean(formPatch.category),
    },
    {
      label: 'Brand',
      rawKeys: ['brand'],
      isResolved: () => Boolean(formPatch.brand),
    },
    {
      label: 'Model',
      rawKeys: ['model'],
      isResolved: () => Boolean(formPatch.model),
    },
    {
      label: 'Vendors',
      rawKeys: ['vendors'],
      isResolved: () => Boolean(formPatch.selectedVendorIds?.length),
    },
  ];

  for (const check of requiredChecks) {
    if (check.isResolved()) continue;
    const providedRaw = check.rawKeys
      .map((k) => String(rawHeader[k] ?? '').trim())
      .find((v) => v.length > 0);
    const masterHit = masterResolved.resolutions.find(
      (r) =>
        (check.label === 'Vendors' ? r.field === 'vendors' : r.field === check.rawKeys[0]) &&
        r.importedValue?.trim()
    );
    const providedValue = providedRaw || masterHit?.importedValue?.trim() || '';
    if (providedValue) {
      fieldsUnresolved.push({ field: check.label, providedValue });
      warnings.push(
        `${check.label}: Provided "${providedValue}" — could not be resolved to ERP master / form value`
      );
    } else {
      fieldsNotProvided.push(check.label);
      warnings.push(`${check.label}: Not provided in source`);
    }
  }

  for (const label of unsupportedQuotationFields) {
    warnings.push(`Unsupported quotation field: ${label}`);
  }

  const needsManualReview =
    warnings.length > 0 ||
    partfinder.resolutions.some(
      (r) => r.status === 'unresolved' || r.status === 'ambiguous'
    );

  return {
    formPatch,
    items: partfinder.items,
    warnings,
    errors,
    summary: {
      itemCount: items.length,
      vendorCount: formPatch.selectedVendorIds?.length ?? 0,
      matchedFields,
      unmatchedFields: unmatchedFields.filter(
        (f) => !unsupportedQuotationFields.some((u) => u.toLowerCase() === f.toLowerCase())
      ),
      fieldsNotProvided,
      fieldsUnresolved,
      unsupportedQuotationFields,
    },
    resolutions: vesselResolution
      ? [vesselResolution, ...masterResolved.resolutions]
      : masterResolved.resolutions,
    extractedFields: buildExtractedFields(rawHeader),
    partfinderResolutions: partfinder.resolutions,
    source: 'excel',
    needsManualReview,
  };
}

export { RFQ_ITEM_COLUMNS };
